import * as React from "react";
import { useGLTF } from "@react-three/drei";
import type { GroupProps } from "@react-three/fiber";
import { useEffect, useMemo } from "react";
import * as THREE from "three";
import radioGlbUrl from "../assets/spirit_box_radio.glb";

type SpiritBoxRadioProps = GroupProps & {
  /** Auto-fit the model so its height ~= `fit` world units. */
  fit?: number;
  /** IBL contribution multiplier for all standard materials inside the model. */
  envMapIntensity?: number;
};

const RADIO_PATH = radioGlbUrl;

export default function SpiritBoxRadio({
  fit = 5,
  envMapIntensity = 0,
  ...props
}: SpiritBoxRadioProps) {
  const { scene } = useGLTF(RADIO_PATH);

  const sceneClone = useMemo(() => {
    const clone = scene.clone(true);
    clone.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        const materials = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];
        for (const mat of materials) {
          if (mat instanceof THREE.MeshStandardMaterial) {
            mat.envMapIntensity = envMapIntensity;
            // Crisp filtering on every texture map, not just base color —
            // anisotropy + trilinear mips keep the keypad/grille sharp at
            // grazing camera angles (the orbit hits many of them). Base
            // color stays sRGB; data maps (normal/roughness/metal) stay
            // linear, which the loader sets — don't override their space.
            for (const map of [
              mat.map,
              mat.normalMap,
              mat.roughnessMap,
              mat.metalnessMap,
              mat.aoMap,
            ]) {
              if (!map) continue;
              map.anisotropy = 16;
              map.minFilter = THREE.LinearMipmapLinearFilter;
              map.magFilter = THREE.LinearFilter;
              map.needsUpdate = true;
            }
            if (mat.map) mat.map.colorSpace = THREE.SRGBColorSpace;
          }
        }
      }
    });
    return clone;
    // envMapIntensity intentionally excluded — tracked reactively in the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene]);

  // Reactive update so Leva tuning of envMapIntensity takes effect without rebuild.
  useEffect(() => {
    sceneClone.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        if (mat instanceof THREE.MeshStandardMaterial) {
          mat.envMapIntensity = envMapIntensity;
        }
      }
    });
  }, [sceneClone, envMapIntensity]);

  const { fitScale, pivotOffset } = useMemo(() => {
    const box = new THREE.Box3().setFromObject(sceneClone);
    const size = box.getSize(new THREE.Vector3());
    const height = size.y;

    const center = box.getCenter(new THREE.Vector3());
    const scale = Number.isFinite(height) && height > 0 ? fit / height : 1;

    const offset: [number, number, number] = [-center.x, -center.y, -center.z];

    return { fitScale: scale, pivotOffset: offset };
  }, [sceneClone, fit]);

  return (
    <group {...props}>
      <group scale={fitScale}>
        <primitive object={sceneClone} position={pivotOffset} />
      </group>
    </group>
  );
}

useGLTF.preload(RADIO_PATH);
